import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL, ICombineHsaSearchResponse, ICustomPageCombineHsaSearchResponse } from '@/dtos';

import { CombineHSASectionType, HSAAccountStatus, HSACombineAccount } from '../types/combineHSA.types';

const COMBINE_HSA_SEARCH_KEY = 'COMBINE_HSA_SEARCH';
const SEARCH_PAGE_SIZE = 100;

const FROM_ALLOWED_STATUSES = [
  HSAAccountStatus.OPEN,
  HSAAccountStatus.PENDING_CLOSED,
  HSAAccountStatus.CLOSED,
];

const TO_ALLOWED_STATUSES = [HSAAccountStatus.OPEN, HSAAccountStatus.PENDING];

interface AutoSearchParams {
  accountNumber: string;
  employeeId: string;
  ssn: string;
  firstName?: string;
  lastName?: string;
  sectionType: CombineHSASectionType;
}

interface ManualSearchParams {
  accountNumber: string;
  employeeId: string;
  ssn: string;
  searchValue: string;
  sectionType: CombineHSASectionType;
}

const getAllowedStatuses = (sectionType: CombineHSASectionType) =>
  sectionType === CombineHSASectionType.FROM ? FROM_ALLOWED_STATUSES : TO_ALLOWED_STATUSES;

const formatAccount = (dto: ICombineHsaSearchResponse): HSACombineAccount => ({
  accountId: dto.account!.account_id as unknown as string,
  accountNumber: dto.account!.number as string,
  employeeId: `${dto.employee_id || ''}`,
  personFirstName: dto.first_name || '',
  personLastName: dto.last_name || '',
  ssn: dto.ssn || '',
  birthOn: dto.birth_on as unknown as string,
  employerName: dto.employer_name || '',
  partnerName: dto.partner_name || '',
  accountStatus: dto.account!.status as unknown as HSAAccountStatus,
  availableBalance: parseFloat(`${dto.account!.available_balance || '0'}`),
  investorAccountBalance: dto.investor_account
    ? parseFloat(`${dto.investor_account.balance || '0'}`)
    : undefined,
});

const getPinnedAccounts = (accountNumber: string, accounts: HSACombineAccount[]) => {
  const matchedAccount = accounts.find((account) => account.accountNumber === accountNumber);

  if (!matchedAccount) {
    return accounts;
  }

  return [matchedAccount, ...accounts.filter((account) => account.accountNumber !== accountNumber)];
};

export const useAutoSearchHSACombineAccounts = ({
  accountNumber,
  employeeId,
  ssn,
  firstName,
  lastName,
  sectionType,
}: AutoSearchParams) => {
  const allowedStatuses = getAllowedStatuses(sectionType);

  const { data, isLoading } = useQuery(
    [COMBINE_HSA_SEARCH_KEY, employeeId, ssn, firstName, lastName, sectionType],
    () =>
      api.get<ICustomPageCombineHsaSearchResponse>(API_URL.EMPLOYEES_COMBINE_HSA_SEARCH, {
        params: { ssn, first_name: firstName, last_name: lastName, size: SEARCH_PAGE_SIZE, page: 0 },
      }),
    { enabled: !!employeeId && !!ssn, retry: false }
  );

  const mapped = (data?.data?.content ?? []).filter((dto) => !!dto.account).map(formatAccount);
  const filtered = mapped.filter((account) => allowedStatuses.includes(account.accountStatus));

  return {
    data: getPinnedAccounts(accountNumber, filtered),
    isLoading,
  };
};

export const useManualSearchHSACombineAccounts = ({
  accountNumber,
  employeeId,
  ssn,
  searchValue,
  sectionType,
}: ManualSearchParams) => {
  const normalizedSearchValue = searchValue.trim();
  const enabled = normalizedSearchValue.length >= 3 && !!employeeId && !!ssn;
  const allowedStatuses = getAllowedStatuses(sectionType);

  const { data, isLoading } = useQuery(
    [COMBINE_HSA_SEARCH_KEY, employeeId, ssn, normalizedSearchValue, sectionType],
    () =>
      api.get<ICustomPageCombineHsaSearchResponse>(API_URL.EMPLOYEES_COMBINE_HSA_SEARCH, {
        params: {
          ssn,
          search_value: normalizedSearchValue,
          size: SEARCH_PAGE_SIZE,
          page: 0,
        },
      }),
    { enabled, retry: false }
  );

  const mapped = (data?.data?.content ?? []).filter((dto) => !!dto.account).map(formatAccount);
  const filtered = mapped.filter((account) => allowedStatuses.includes(account.accountStatus));

  return {
    data: getPinnedAccounts(accountNumber, filtered),
    enabled,
    isLoading,
  };
};
